import { useQueryClient } from '@tanstack/react-query';
import {
  deleteSeasonalRule,
  DeleteSeasonalRuleArgs,
} from 'components/domain/property/seasonality/deleteSeasonalRule';
import { SEASONAL_RULES_BASE_QUERY_KEY } from 'components/domain/property/seasonality/seasonalRulesQuery';
import useAppMutation from 'hooks/useAppMutation';
import useNotify from 'hooks/useNotify';
import { BaseErrorResponse } from 'models/_base';
import { useTranslation } from 'react-i18next';
import { getErrorMessage } from 'utils/axiosExceptionUtils';

interface Args {
  mutationKey: (string | number)[];
  successCallback?: () => void;
}

const useDeleteSeasonalRuleMutation = ({
  mutationKey,
  successCallback,
}: Args) => {
  const queryClient = useQueryClient();
  const { notifySuccess, notifyError } = useNotify();
  const { t } = useTranslation();

  const { mutateAsync: deleteRule, isLoading: isDeleting } = useAppMutation(
    mutationKey,
    (args: DeleteSeasonalRuleArgs) => deleteSeasonalRule(args),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [SEASONAL_RULES_BASE_QUERY_KEY],
        });
        notifySuccess(t('pageProperty.seasonality.deleteModal.deleteSuccess'));
        successCallback?.();
      },
      onError: (error: BaseErrorResponse) => {
        notifyError(
          getErrorMessage({
            apiErrorCode: error?.response?.data?.apiErrorCode,
            apiErrorMessage: error?.response?.data?.apiErrorMessage,
            baseKey: 'pageProperty.apiErrorCodes.',
            t,
          }),
        );
      },
    },
  );

  return { deleteRule, isDeleting };
};

export default useDeleteSeasonalRuleMutation;
